import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgeCheck, Check, Clock, Copy, MapPin, ShieldCheck, Tag, Truck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SEO } from "../components/SEO";
import { Reveal, MaskedLines } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { ProductCard } from "../components/ProductCard";
import { api, fmt, priceLabel, track } from "../lib/api";
import { FAQS, GOOGLE, NEIGHBORHOODS, ORDER_STEPS, SITE, TRUST_POINTS, jsonLd } from "../config/site";
import { GoogleMark, Stars } from "../components/GoogleMark";
import { ReviewsSection } from "../components/ReviewsSection";
import { toast } from "sonner";

// Editorial ribbon: Google logo + gold stars + verified review count (repeats).
const GOOGLE_RIBBON = [0, 1, 2, 3].map((i) => (
  <span key={i} className="flex items-center gap-4">
    <GoogleMark className="h-6 w-6 md:h-7 md:w-7" />
    <Stars value={5} className="h-5 w-5 md:h-6 md:w-6" />
    <span>{GOOGLE.count} Google Reviews</span>
  </span>
));

const ICONS = { MapPin, BadgeCheck, Tag, Truck };

export default function HomePage() {
  const [data, setData] = useState({ featured: [], deals: [], spotlight: null });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.home().then(setData).catch(() => {});
    api.categories().then((d) => setCategories((d.items || []).filter((c) => !c.parentId))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Premium Cannabis, Honest Prices — DC Delivery & Pickup"
        path="/"
        jsonLd={[jsonLd.organization, jsonLd.localBusiness, {
          "@context": "https://schema.org", "@type": "WebSite", name: SITE.name, url: SITE.url,
          potentialAction: { "@type": "SearchAction", target: `${SITE.url}/search?q={query}`, "query-input": "required name=query" },
        }]}
      />
      <Hero flyers={data.flyers || []} />
      <PromoBanner />
      <div className="bg-black py-3.5" data-testid="google-reviews-ribbon">
        <Marquee
          items={GOOGLE_RIBBON}
          itemClassName="font-display text-lg md:text-2xl font-black text-white"
          separator=""
        />
      </div>
      <CategoryBento categories={categories} />
      <TrustStrip />
      <ReviewsSection />
      <BestSellers products={data.featured} />
      <DealsRow flyers={data.flyers || []} />
      <Spotlight product={data.spotlight} />
      <HowToOrder />
      <DeliveryPreview />
      <FaqPreview />
      <Newsletter />
    </div>
  );
}

/* ---------------- Hero ---------------- */

function Hero({ flyers = [] }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const [flyerIndex, setFlyerIndex] = useState(0);
  const [flyersPaused, setFlyersPaused] = useState(false);

  useEffect(() => {
    if (flyersPaused || flyers.length < 2) return undefined;
    const t = setInterval(() => setFlyerIndex((i) => (i + 1) % flyers.length), 4000);
    return () => clearInterval(t);
  }, [flyersPaused, flyers.length]);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);

  return (
    <section ref={ref} data-testid="hero" className="relative overflow-hidden bg-brand-forest text-brand-cream">
      <motion.div style={{ y: yGlow }} aria-hidden="true" className="absolute -right-40 -top-40 h-[36rem] w-[36rem] rounded-full bg-brand-gold/15 blur-[120px]" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 select-none overflow-hidden">
        <span className="block translate-y-[30%] whitespace-nowrap font-display text-[22vw] font-black leading-none text-white/[0.04]">
          CANNACHEAP
        </span>
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-24 pt-16 sm:px-6 md:pt-24 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:pb-32">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-brand-gold/40 bg-brand-gold/10 px-4 py-1.5 text-[11px] font-display font-bold uppercase tracking-[0.25em] text-brand-gold"
          >
            <ShieldCheck className="h-3.5 w-3.5" /> 21+ · Washington DC
          </motion.div>

          <MaskedLines
            lines={["High-Quality Cannabis,", "Prices You", "Can Rely On."]}
            className="text-balance text-5xl font-black leading-[0.95] sm:text-6xl lg:text-7xl"
            lineClassName=""
            delay={0.25}
          />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="mt-7 max-w-md text-base leading-relaxed text-brand-cream/75 md:text-lg"
          >
            CannaCheap offers high-quality cannabis products at prices people can rely on — with
            reliable pickup & delivery options across Washington, D.C. in 30–90 minutes.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/shop"
              data-testid="hero-shop-cta"
              onClick={() => track("cta_click", { cta: "hero_shop" })}
              className="group inline-flex items-center gap-2.5 rounded-full bg-brand-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-forest transition-transform hover:scale-[1.04]"
            >
              Shop the Menu
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/how-to-order"
              data-testid="hero-howto-cta"
              className="rounded-full border border-white/25 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-cream transition-colors hover:border-brand-gold hover:text-brand-gold"
            >
              How It Works
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-xs text-brand-cream/60"
          >
            <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-brand-gold" /> 30–90 min delivery</span>
            <span className="flex items-center gap-2"><Truck className="h-4 w-4 text-brand-gold" /> Free delivery {fmt(SITE.freeDeliveryMinimum)}+ · code DC80</span>
            <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-gold" /> DC · MD · VA</span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto max-w-sm lg:ml-auto"
            data-testid="hero-deals-flyer"
            onMouseEnter={() => setFlyersPaused(true)}
            onMouseLeave={() => setFlyersPaused(false)}
          >
            {flyers.length > 0 ? (
              <>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={flyerIndex % flyers.length}
                    initial={{ opacity: 0, y: 24, rotate: 1.5 }}
                    animate={{ opacity: 1, y: 0, rotate: 0 }}
                    exit={{ opacity: 0, y: -24, rotate: -1.5 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <Link
                      to={`/product/${flyers[flyerIndex % flyers.length].slug}`}
                      data-testid="hero-deal-card"
                      className="group relative block overflow-hidden rounded-[2rem] border border-brand-gold/40 shadow-2xl shadow-black/40 ring-1 ring-brand-gold/30 transition-transform hover:scale-[1.02]"
                    >
                      {flyers[flyerIndex % flyers.length].image ? (
                        <img
                          src={flyers[flyerIndex % flyers.length].image}
                          alt={`${flyers[flyerIndex % flyers.length].name} — CannaCheap DC deal flyer`}
                          className="aspect-[4/5] w-full object-cover"
                        />
                      ) : (
                        <span className="flex aspect-[4/5] w-full items-center justify-center bg-brand-pine/60 p-10 text-center font-display text-2xl font-black text-brand-cream">
                          {flyers[flyerIndex % flyers.length].name}
                        </span>
                      )}
                      <span className="absolute left-4 top-4 rounded-full bg-brand-gold px-4 py-1.5 text-[10px] font-display font-bold uppercase tracking-[0.22em] text-brand-forest shadow-lg">
                        Special Deal
                      </span>
                      <span className="absolute inset-x-4 bottom-4 flex items-center justify-center gap-2 rounded-full bg-brand-forest/95 px-4 py-3.5 text-sm font-display font-bold uppercase tracking-[0.18em] text-brand-cream shadow-lg backdrop-blur transition-transform group-hover:scale-[1.02]">
                        Get This Deal <ArrowRight className="h-4 w-4" />
                      </span>
                    </Link>
                  </motion.div>
                </AnimatePresence>
                {flyers.length > 1 && (
                  <div className="mt-6 flex justify-center gap-2">
                    {flyers.map((_, i) => (
                      <button
                        key={i}
                        data-testid={`hero-flyer-dot-${i}`}
                        aria-label={`Show deal flyer ${i + 1}`}
                        onClick={() => setFlyerIndex(i)}
                        className={`h-2 rounded-full transition-all ${
                          i === flyerIndex % flyers.length ? "w-8 bg-brand-gold" : "w-2 bg-white/25 hover:bg-white/50"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : null}
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Promo banner (DC80) ---------------- */

function PromoBanner() {
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText("DC80");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  return (
    <section data-testid="promo-banner" className="bg-brand-gold text-brand-forest">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-8 gap-y-4 px-4 py-7 sm:px-6 lg:justify-between lg:px-10">
        <Reveal className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-center lg:text-left">
          <span className="font-display text-2xl font-black uppercase tracking-tight sm:text-3xl">
            Free DC Delivery
          </span>
          <span className="font-display text-lg font-bold text-brand-forest/70 sm:text-xl">
            on all orders $80+
          </span>
        </Reveal>
        <Reveal delay={0.1} className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-forest/60">
            Use code at checkout
          </span>
          <button
            type="button"
            onClick={copyCode}
            data-testid="promo-code-copy"
            aria-label="Copy promo code DC80"
            title="Click to copy"
            className="group flex items-center gap-2.5 rounded-full border-2 border-dashed border-brand-forest/50 bg-brand-cream px-6 py-3 font-display text-xl font-black tracking-[0.25em] text-brand-forest transition-all hover:scale-[1.04] hover:border-brand-forest"
          >
            DC80
            {copied ? (
              <Check className="h-4 w-4 text-brand-moss" />
            ) : (
              <Copy className="h-4 w-4 opacity-50 transition-opacity group-hover:opacity-100" />
            )}
          </button>
          {copied && <span className="text-xs font-bold text-brand-forest">Copied!</span>}
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Trust ---------------- */

function TrustStrip() {
  return (
    <section data-testid="trust-strip" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
      <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        {TRUST_POINTS.map((point, i) => {
          const Icon = ICONS[point.icon];
          return (
            <Reveal key={point.title} delay={i * 0.08}>
              <div className="border-l-2 border-brand-gold pl-5">
                <Icon className="h-6 w-6 text-brand-pine" aria-hidden="true" />
                <h3 className="mt-4 font-display text-lg font-bold text-brand-forest">{point.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-ink/60">{point.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

/* ---------------- Category grid ---------------- */

function CategoryBento({ categories }) {
  if (!categories.length) return null;
  return (
    <section data-testid="shop-by-category" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
      <Reveal className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-brand-golddeep">The Menu</p>
          <h2 className="mt-3 text-4xl font-black text-brand-forest sm:text-5xl">Shop by Category</h2>
        </div>
        <Link to="/shop" data-testid="category-view-all" className="group flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-pine">
          View all <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-3 lg:gap-x-8">
        {categories.slice(0, 6).map((cat, i) => (
          <Reveal key={cat.id} delay={(i % 3) * 0.06}>
            <Link
              to={`/category/${cat.id}`}
              data-testid={`category-card-${cat.slug}`}
              className="group flex h-full flex-col items-center p-2 text-center sm:p-4"
            >
              <div className="flex h-44 w-full items-center justify-center sm:h-64">
                {cat.image ? (
                  <img
                    src={cat.image}
                    alt={`${cat.name} category`}
                    loading="lazy"
                    className="img-blend max-h-full w-auto max-w-full object-contain transition-transform duration-500 group-hover:scale-105"
                  />
                ) : (
                  <span className="font-display text-4xl font-black text-brand-ink/15">CC</span>
                )}
              </div>
              <h3 className="mt-5 font-display text-sm font-black uppercase tracking-wide text-brand-forest sm:text-lg">
                {cat.name}
              </h3>
              <span className="mt-2.5 h-0.5 w-8 rounded-full bg-brand-gold transition-all duration-300 group-hover:w-14" />
              <p className="mt-2 text-xs text-brand-ink/50">{cat.productCount} products</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Product sections ---------------- */

function BestSellers({ products }) {
  if (!products.length) return null;
  return (
    <section data-testid="best-sellers" className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-10 lg:pb-24">
      <Reveal className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-brand-golddeep">Customer Favorites</p>
          <h2 className="mt-3 text-4xl font-black text-brand-forest sm:text-5xl">Best Sellers</h2>
        </div>
        <Link to="/shop" data-testid="best-sellers-view-all" className="group flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-pine">
          View all <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <div className="mt-10 grid grid-cols-2 gap-x-4 gap-y-10 lg:grid-cols-4 lg:gap-x-6">
        {products.slice(0, 8).map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
      </div>
    </section>
  );
}

function DealsRow({ flyers = [] }) {
  if (!flyers.length) return null;
  const track = [...flyers, ...flyers];
  return (
    <section data-testid="special-deals" className="overflow-hidden bg-brand-pine py-16 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-brand-gold">Save Today</p>
            <h2 className="mt-3 text-4xl font-black text-brand-cream sm:text-5xl">Special Deals</h2>
          </div>
          <Link to="/shop?on_sale=true" data-testid="deals-view-all" className="group flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-gold">
            All deals <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
      <div className="group relative mt-10" data-testid="deals-flyer-rotator">
        <div
          className="flex w-max animate-marquee gap-5 pl-5 group-hover:[animation-play-state:paused]"
          style={{ animationDuration: "70s" }}
        >
          {track.map((p, i) => (
            <Link
              key={`${p.id}-${i}`}
              to={`/product/${p.slug}`}
              {...(i < flyers.length ? { "data-testid": `deal-flyer-${p.id}` } : {})}
              aria-hidden={i >= flyers.length}
              tabIndex={i >= flyers.length ? -1 : 0}
              className="relative block w-56 shrink-0 overflow-hidden rounded-2xl border border-white/10 shadow-xl shadow-black/30 transition-transform duration-300 hover:scale-[1.03] sm:w-72"
            >
              {p.image ? (
                <img src={p.image} alt={p.name} loading="lazy" className="aspect-[4/5] w-full object-cover" />
              ) : (
                <div className="flex aspect-[4/5] w-full items-center justify-center bg-brand-forest font-display text-brand-cream/40">
                  {p.name}
                </div>
              )}
              {p.price > 0 && (
                <span className="absolute bottom-3 left-3 rounded-full bg-brand-gold px-3.5 py-1.5 font-display text-sm font-black text-brand-forest shadow-lg">
                  {fmt(p.price)}
                </span>
              )}
            </Link>
          ))}
        </div>
        <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-brand-pine to-transparent" />
        <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-brand-pine to-transparent" />
      </div>
    </section>
  );
}

function Spotlight({ product }) {
  if (!product) return null;
  return (
    <section data-testid="featured-product" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10 lg:py-28">
      <div className="grid items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            <div aria-hidden="true" className="absolute -left-6 -top-6 h-24 w-24 rounded-tl-[2rem] border-l-2 border-t-2 border-brand-gold" />
            <div className="overflow-hidden rounded-[2rem] border border-brand-line">
              <img src={product.image} alt={product.name} loading="lazy" className="aspect-[4/5] w-full object-cover" />
            </div>
          </div>
        </Reveal>
        <div>
          <MaskedLines
            as="h2"
            lines={["Worth", "Every", "Dollar."]}
            className="text-5xl font-black text-brand-forest sm:text-6xl"
          />
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-brand-ink/65">
              {product.descriptionText?.slice(0, 220) ||
                "Hand-picked, carefully sourced, and priced the way it should be. This week's featured drop won't sit long."}
              {product.descriptionText ? "…" : ""}
            </p>
            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-display text-4xl font-black text-brand-pine">{priceLabel(product)}</span>
              {product.comparePrice && (
                <span className="text-lg text-brand-ink/40 line-through">{fmt(product.comparePrice)}</span>
              )}
            </div>
            <Link
              to={`/product/${product.slug}`}
              data-testid="spotlight-cta"
              className="group mt-8 inline-flex items-center gap-2.5 rounded-full bg-brand-pine px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-cream transition-transform hover:scale-[1.03]"
            >
              View Product <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- How to order ---------------- */

function HowToOrder() {
  return (
    <section data-testid="how-to-order-preview" className="bg-brand-forest py-16 text-brand-cream lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <Reveal>
          <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-brand-gold">Simple by Design</p>
          <h2 className="mt-3 text-4xl font-black sm:text-5xl">How to Order</h2>
        </Reveal>
        <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {ORDER_STEPS.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.08}>
              <span className="font-display text-6xl font-black text-brand-gold/30">{step.n}</span>
              <h3 className="mt-4 font-display text-xl font-bold">{step.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-brand-cream/65">{step.text}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.3} className="mt-12">
          <Link
            to="/shop"
            data-testid="howto-start-order"
            className="group inline-flex items-center gap-2.5 rounded-full bg-brand-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-forest transition-transform hover:scale-[1.04]"
          >
            Start an Order <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Delivery preview ---------------- */

function DeliveryPreview() {
  const dc = NEIGHBORHOODS.filter((n) => n.region === "DC").slice(0, 8);
  return (
    <section data-testid="delivery-preview" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10 lg:py-28">
      <div className="grid items-start gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-brand-golddeep">Delivery & Pickup</p>
          <h2 className="mt-3 text-4xl font-black text-brand-forest sm:text-5xl">We Come to You.</h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-brand-ink/65">
            Same-day delivery across every DC quadrant — plus Bethesda, Silver Spring, Takoma Park,
            Hyattsville, College Park, Arlington and Alexandria. Most orders land in 30–90 minutes.
          </p>
          <Link
            to="/delivery"
            data-testid="delivery-areas-cta"
            className="group mt-8 inline-flex items-center gap-2.5 rounded-full border-2 border-brand-pine px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-pine transition-colors hover:bg-brand-pine hover:text-brand-cream"
          >
            All Delivery Areas <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="flex flex-wrap gap-2.5">
            {dc.map((n) => (
              <Link
                key={n.slug}
                to={`/areas/${n.slug}`}
                data-testid={`area-chip-${n.slug}`}
                className="rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-medium text-brand-ink/75 transition-all hover:border-brand-gold hover:bg-brand-gold/10 hover:text-brand-forest"
              >
                {n.name}
              </Link>
            ))}
            <span className="rounded-full bg-brand-pine px-5 py-2.5 text-sm font-bold text-brand-cream">
              + all of DC, MD & NoVA
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- FAQ preview ---------------- */

function FaqPreview() {
  return (
    <section data-testid="faq-preview" className="border-t border-brand-line bg-white py-16 lg:py-24">
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <Reveal>
          <p className="text-xs font-display font-bold uppercase tracking-[0.3em] text-brand-golddeep">Good to Know</p>
          <h2 className="mt-3 text-4xl font-black text-brand-forest">Questions, Answered</h2>
        </Reveal>
        <Reveal delay={0.1} className="mt-8">
          <Accordion type="single" collapsible>
            {FAQS.slice(0, 4).map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-brand-line">
                <AccordionTrigger data-testid={`faq-preview-q-${i}`} className="text-left font-display text-base font-bold text-brand-forest">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-brand-ink/65">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
        <Reveal delay={0.15} className="mt-8">
          <Link to="/faq" data-testid="faq-view-all" className="group inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-pine">
            Full FAQ <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Newsletter ---------------- */

function Newsletter() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  void navigate;

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.newsletter(email);
      track("newsletter_signup", { email });
      toast.success("You're on the list — deals incoming.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="newsletter-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-10 lg:py-24">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] border border-brand-gold/40 bg-brand-pine px-6 py-14 text-brand-cream sm:px-12 lg:px-16">
          <div aria-hidden="true" className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-brand-gold/20 blur-[90px]" />
          <div className="relative grid items-center gap-8 lg:grid-cols-2">
            <div>
              <h2 className="text-3xl font-black sm:text-4xl">Deals land first in your inbox.</h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-brand-cream/70">
                New drops, daily specials and subscriber-only pricing. No spam — just the good stuff.
              </p>
            </div>
            <form onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
              <label htmlFor="newsletter-email" className="sr-only">Email address</label>
              <input
                id="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                data-testid="newsletter-email-input"
                className="flex-1 rounded-full border border-white/20 bg-white/10 px-6 py-4 text-sm text-brand-cream placeholder:text-brand-cream/40 outline-none focus:border-brand-gold"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="newsletter-signup-button"
                className="rounded-full bg-brand-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.15em] text-brand-forest transition-transform hover:scale-[1.03] disabled:opacity-50"
              >
                {busy ? "Joining…" : "Sign Up"}
              </button>
            </form>
          </div>
        </div>
      </Reveal>
    </section>
  );
}
